<template>
    <div
        :class="{
            'compare-table tw-bg-white tw-scroll-smooth': true,
            'compare-table--item-insertable': newItemIsInsertable,
            [`compare-table--columns_${items.length}/${maxItems}`]: true,
            [`compare-table--has-more-then-one-item`]: items.length > 1,
        }"
    >
        <dk-table sticky-headers="monoaxial">
            <template slot="thead">
                <tr class="compare-table__vehicle-info">
                    <th
                        v-for="(item, i) in itemList"
                        :key="`${item.modelId}-${i}`"
                        :class="[
                            'table__header',
                            'tw-bg-white',
                            'tw-border-l tw-border-r tw-border-solid tw-border-border-color',
                            'tw-p-0',
                            'tw-align-middle',
                            ...(isEmpty(item)
                                ? ['table__header--align_center', ...(shouldHideLastColumn(i) ? ['u-hidden'] : [])]
                                : ['table__header--align_left']),
                        ]"
                    >
                        <template v-if="isEmpty(item)">
                            <a
                                href="#0"
                                class="compare-table__add-vehicle tw-block max-lg:tw-text-basic lg:tw-text-placeholder hover:lg:tw-text-basic"
                                @click.prevent="onAddVehicleClick"
                            >
                                <dk-icon name="ds-plus-filled" size="s" class="lg:!tw-hidden" color="brand-primary" />
                                <dk-icon
                                    name="ds-plus-filled"
                                    size="m"
                                    class="tw-hidden lg:tw-block"
                                    color="brand-primary"
                                />
                                <div class="body-lg tw-font-bold tw-mt-2">
                                    {{ $t('compare-table.add-vehicle') }}
                                </div>
                            </a>
                        </template>
                        <template v-else>
                            <compare-vehicle-info
                                :vehicle="getVehicleWithIndex(item, i)"
                                :offers="offers[i]"
                                :disabled="busy"
                                @selection="onMenuClick"
                            />
                        </template>
                    </th>
                </tr>
                <tr v-if="newItemIsInsertable" class="compare-table__add-vehicle-btn lg:tw-hidden">
                    <th :colspan="colspan" class="table__header--align_center table__cell--valign_middle">
                        <a
                            href="#0"
                            class="tw-flex tw-items-center tw-justify-center tw-py-4 tw-text-lg tw-w-full tw-border-b tw-border-solid tw-border-border-color tw-bg-white"
                            @click.prevent="onAddVehicleClick"
                        >
                            <dk-icon name="ds-plus-filled" size="s" class="tw-mr-2" color="brand-primary" />
                            <span>{{ $t('compare-table.add-vehicle') }}</span>
                        </a>
                    </th>
                </tr>
            </template>

            <!-- @slot Table body content -->
            <slot>
                <tr class="compare-table__pricing-row-wrapper">
                    <th
                        :colspan="colspan"
                        class="compare-table__pricing-thead-wrapper table__header--align_left table__cell--valign_middle"
                    >
                        <table class="compare-table__pricing-table-wrapper tw-w-full">
                            <tr
                                v-for="(pricings, i) in offers"
                                :key="i"
                                :class="{
                                    table__row: true,
                                    'compare-table__pricing': true,
                                    'tw-bg-secondary-light': i % 2 === 0,
                                }"
                            >
                                <td
                                    v-for="(pricing, j) in pricings"
                                    :key="j"
                                    :class="{
                                        'table__cell tw-px-4 tw-py-2 lg:tw-py-3': true,
                                        'table__cell--bordered': true,
                                        'tw-align-middle': true,
                                        'tw-hidden': shouldHideLastColumn(j),
                                    }"
                                    :style="{ width: 100 / colspan + '%' }"
                                >
                                    <h3
                                        class="compare-table__cell-label compare-table__pricing-label caption tw-text-base-subtle tw-leading-xs"
                                    >
                                        {{ pricing.type && pricing.type.text }}
                                    </h3>
                                    <p
                                        v-if="isPollingPending"
                                        class="compare-table__cell-value caption tw-font-bold tw-text-basic"
                                    >
                                        {{ $t('ds-offer-card.message.loading') }}
                                    </p>
                                    <p
                                        v-else-if="!isPollingPending && pricing.empty"
                                        class="compare-table__pricing-empty caption tw-font-bold tw-text-basic"
                                    >
                                        {{ $options.filters.dash() }}
                                    </p>
                                    <template v-else>
                                        <div
                                            class="compare-table__cell-value compare-table__pricing-value tw-flex tw-flex-col tw-gap-1"
                                        >
                                            <span
                                                v-if="pricing.originalPrice"
                                                class="caption tw-text-basic tw-line-through tw-mr-1"
                                            >
                                                {{ pricing.originalPrice }}
                                            </span>
                                            <span class="tw-flex tw-items-center tw-gap-1">
                                                <span v-if="pricing.pricePrefix" class="caption tw-mr-1">
                                                    {{ pricing.pricePrefix }}
                                                </span>
                                                <span class="body-sm tw-text-basic tw-font-bold">
                                                    {{ pricing.finalPrice || pricing.originalPrice }}
                                                </span>
                                                <span class="micro tw-text-basic tw-font-bold">{{
                                                    pricing.priceSuffix
                                                }}</span>
                                            </span>
                                        </div>
                                    </template>
                                </td>
                            </tr>
                        </table>
                    </th>
                </tr>
                <template v-for="(section, index) in sections">
                    <tr :id="section.id" :key="`${section.id}-${index}`" class="compare-table__section-name">
                        <th :colspan="colspan" scope="row" class="table__header tw-px-4 tw-pt-8 tw-pb-4">
                            <h2 class="compare-table__section-name-title subtitle-lg !tw-font-semibold tw-text-left">
                                {{ section.name }}
                            </h2>
                        </th>
                    </tr>

                    <template v-for="(feature, i) in section.features">
                        <template v-if="canShowRow(section, i, feature)">
                            <tr
                                :key="`${feature.key}-${i}-items`"
                                :class="{
                                    table__row: true,
                                    'table__row--bordered':
                                        i === section.features.length - 1 && showAllOptions && section.id === 'options',
                                    'tw-bg-secondary-light': i % 2 === 0,
                                }"
                            >
                                <td
                                    v-for="(item, j) in itemList"
                                    :key="`${item.modelId}-${j}`"
                                    :class="{
                                        'table__cell tw-px-4 tw-py-2 lg:tw-py-3': true,
                                        'table__cell--bordered': true,
                                        'tw-align-middle': true,
                                        'tw-hidden': shouldHideLastColumn(j),
                                    }"
                                >
                                    <h3
                                        class="compare-table__cell-label caption tw-text-base-subtle tw-leading-xs"
                                        v-html="feature.name"
                                    ></h3>
                                    <template v-if="section.id === 'electricFeatures'">
                                        <div
                                            v-if="
                                                getElectricIcon(item, feature)[0] &&
                                                    !(
                                                        getElectricIcon(item, feature)[0] === 'ds-close-default' ||
                                                        getElectricIcon(item, feature)[0] === 'ds-checkmark-empty'
                                                    )
                                            "
                                            class="tw-flex tw-items-center 1"
                                        >
                                            <span class="tw-mr-auto body-sm tw-font-bold">{{
                                                getElectricText(item, feature)
                                            }}</span>
                                            <dk-icon
                                                :name="getElectricIcon(item, feature)[0]"
                                                size="s"
                                                class="tw-shrink-0"
                                                :class="getElectricIcon(item, feature)[1]"
                                            />
                                        </div>
                                        <template v-else>
                                            <div class="tw-flex tw-items-center 2">
                                                <dk-icon
                                                    v-if="getElectricIcon(item, feature)[0]"
                                                    :name="getElectricIcon(item, feature)[0]"
                                                    class="tw-mr-1 tw-shrink-0"
                                                    :class="getElectricIcon(item, feature)[1]"
                                                    size="s"
                                                />
                                                <span class="body-sm tw-font-bold">
                                                    {{ getElectricText(item, feature) }}</span>
                                            </div>
                                        </template>
                                    </template>
                                    <template v-else-if="section.id === 'options'">
                                        <div class="tw-flex tw-items-center 3">
                                            <dk-icon
                                                v-if="getOptionIcon(item, feature)[0]"
                                                :name="getOptionIcon(item, feature)[0]"
                                                class="tw-shrink-0"
                                                :class="getOptionIcon(item, feature)[1]"
                                                size="s"
                                            />
                                            <span class="tw-ml-1 body-sm tw-font-bold">
                                                {{ getOptionText(item, feature) }}</span>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <template v-if="feature.key === 'dimensions'">
                                            <div
                                                :class="{
                                                    'compare-dimensions tw-flex tw-flex-col lg:tw-grid lg:tw-grid-cols-2 tw-gap-6': true,
                                                    'compare-dimensions--has-dimensions': hasDimensions(
                                                        item.dimensions
                                                    ),
                                                }"
                                            >
                                                <div
                                                    class="compare-dimensions__side-view tw-p-4 tw-text-center lg:tw-flex lg:tw-items-center"
                                                >
                                                    <span
                                                        v-if="hasDimensions(item.dimensions)"
                                                        class="compare-dimensions__length tw-absolute tw-bottom-2 tw-left-1/2 tw-transform -tw-translate-x-1/2 tw-translate-y-1/2 tw-px-2 tw-bg-secondary-light caption"
                                                        v-html="item.dimensions.length.value"
                                                    ></span>
                                                    <img
                                                        :alt="`vehicle-${item.makeUrlCode}-side-view-${j}`"
                                                        class="compare-dimensions__image tw-max-w-full tw-h-auto"
                                                        src="/wp-content/themes/drivek/v4/assets/img/vehicle-side-view.png"
                                                        width="184px"
                                                        height="92px"
                                                    >
                                                </div>
                                                <div class="compare-dimensions__front-view tw-p-4 tw-text-center">
                                                    <template v-if="hasDimensions(item.dimensions)">
                                                        <span
                                                            class="compare-dimensions__width tw-absolute tw-bottom-2 tw-left-1/2 tw-transform -tw-translate-x-1/2 tw-translate-y-1/2 tw-z-10 tw-px-2 tw-bg-secondary-light caption"
                                                            v-html="item.dimensions.width.value"
                                                        ></span>
                                                        <span
                                                            class="compare-dimensions__height tw-absolute tw-left-4 tw-top-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2 -tw-rotate-90 tw-z-10 tw-px-2 tw-bg-secondary-light caption"
                                                            v-html="item.dimensions.height.value"
                                                        ></span>
                                                    </template>
                                                    <img
                                                        :alt="`vehicle-${item.makeUrlCode}-front-view-${j}`"
                                                        class="compare-dimensions__image tw-h-auto max-lg:tw-max-w-[100px] lg:tw-h-[98px] tw-w-auto"
                                                        src="/wp-content/themes/drivek/v4/assets/img/vehicle-front-view.png"
                                                        width="87px"
                                                        height="72px"
                                                    >
                                                </div>
                                            </div>
                                        </template>
                                        <div
                                            v-else
                                            class="tw-flex max-lg:tw-flex-col lg:tw-justify-between lg:tw-items-center"
                                        >
                                            <span
                                                class="compare-table__cell-value body-sm tw-font-bold"
                                                v-html="$options.filters.dash(item[feature.key])"
                                            ></span>
                                            <dk-rating-dst
                                                v-if="canShowRating(feature, item[feature.key])"
                                                v-model="item[feature.key].rawValue"
                                                :percentage="item[feature.key].percentile"
                                                :type="feature.ratingType"
                                                class="compare-table__rating max-lg:tw-mt-2"
                                            />
                                        </div>
                                    </template>
                                </td>
                            </tr>
                        </template>
                    </template>

                    <tr
                        v-if="section.id === 'options' && !showAllOptions"
                        :key="`${section.id}-see-all`"
                        class="compare-table__section-name"
                    >
                        <th
                            :colspan="colspan"
                            scope="row"
                            class="compare-table__see-all-toggler table__header table__header--align_center table__header--padding_s"
                        >
                            <a
                                href="#0"
                                class="tw-flex tw-items-center tw-justify-between body-md tw-font-bold tw-text-brand-primary"
                                @click.prevent="onSeeAllClick"
                            >
                                <span>{{ $t('compare-table.see-all-options') }}</span>
                                <dk-icon name="ds-chevron-down" size="s" />
                            </a>
                        </th>
                    </tr>
                </template>
            </slot>

            <tr v-if="!shouldHideCta" class="compare-table__compare-quotes">
                <th
                    :colspan="colspan"
                    scope="row"
                    class="tw-fixed tw-bottom-0 tw-left-0 tw-w-full tw-h-auto tw-z-[100] tw-p-4 tw-flex tw-justify-center tw-bg-white tw-shadow-md-up"
                >
                    <dk-button
                        :variant="'accent'"
                        :disabled="busy"
                        type="button"
                        class="max-lg:tw-flex-1"
                        @click="onCompareQuotesClick"
                    >
                        {{ $t('wizard-configure-car-modal.btn-next') }}
                    </dk-button>
                </th>
            </tr>
        </dk-table>
    </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { breakpoint } from '@v4/common/utils/media-query';
import { on, off } from '@v4/common/utils/dom';
import DkTable from '@v4/common/components/organisms/DkTable.vue';
import CompareVehicleInfo from '@v4/compare/components/molecules/CompareVehicleInfo';
import throttle from 'lodash/throttle';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import { dkModalAware } from '@v4/common/components/mixins/modal';

/**
 * Compare table
 *
 * @version 4.0.0
 * @example ./examples/CompareTable.md
 */
export default {
    name: 'CompareTable',
    components: {
        DkTable,
        DkRatingDst: () => ({
            component: import(
                /* webpackChunkName: "DkRatingDst-vue", webpackPreload: true */ '@v4/common/components/molecules/DkRatingDST.vue'
            ),
        }),
        CompareVehicleInfo,
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon'),
        }),
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton'),
        }),
    },
    filters: {
        dash(item) {
            if (!item || (item && typeof item === 'object' && !item.value)) {
                return '–';
            }

            if (item.value) {
                return item.value;
            }

            return item;
        },
    },
    mixins: [gaEventHandler, dkModalAware],
    props: {
        /**
         * Vehicle list
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * Table sections
         */
        sections: {
            type: Array,
            default: () => [],
        },
        /**
         * How many maximum columns will be shown
         */
        maxItems: {
            type: Number,
            default: 3,
        },
        /**
         * Busy state, when the table it's loading
         */
        busy: {
            type: Boolean,
            default: false,
        },
        /**
         * The pricing list
         */
        pricingList: {
            type: Array,
            default: () => [],
        },
        /**
         * Pending polling state, when the price list is fetching
         */
        isPollingPending: {
            type: Boolean,
            default: true,
        },
        /**
         * Google Analytics events
         */
        gaEvents: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            tabIdPrefix: 'tab-',
            colspan: 0,
            isMobile: false,
            tabOptions: {
                fullWidth: true,
                thinyLines: true,
                spaceBetween: 0,
                useUrlFragment: false,
            },
            showAllOptions: false,
        };
    },
    computed: {
        itemList() {
            if (this.newItemIsInsertable) {
                return [...this.items, {}];
            }

            return this.items;
        },
        newItemIsInsertable() {
            return this.items.length < this.maxItems;
        },
        offers() {
            if (this.pricingList.length === 0) {
                return [];
            }

            let pricingList = this.pricingList;

            if (pricingList.length < this.maxItems) {
                const emptyOffers = [...this.pricingList[0].offers].map((item) => ({
                    type: item.type,
                    priceSuffix: '',
                    finalPrice: '',
                    originalPrice: '',
                }));
                pricingList = [...pricingList, { offers: emptyOffers }];
            }

            const offers = pricingList.map((item) => item.offers);

            const pricings = [];
            const maxOffersCount = offers[0].length;
            for (let i = 0; i < maxOffersCount; i += 1) {
                pricings.push(offers.map((offer) => offer[i]));
            }

            return pricings;
        },
        shouldHideCta() {
            if (!this.items.length || this.isPollingPending || !this.pricingList.length) {
                return true;
            }

            return !this.pricingList.some((item) => {
                const hasOffers = item.offers && !!item.offers.length;

                return hasOffers && item.offers.filter((offer) => !offer.empty).some((offer) => offer.active);
            });
        },
    },
    watch: {
        items() {
            this.setColspan();
        },
    },
    created() {
        this.setColspan();

        on(window, 'resize', this.throttledSetColspan);
    },
    destroyed() {
        off(window, 'resize', this.throttledSetColspan);
        off(window, 'scroll', this.throttledScrollSpy);
    },
    mounted() {
        on(window, 'scroll', this.throttledScrollSpy);
    },
    methods: {
        isEmpty,
        canShowRow(section, index, feature) {
            return (
                section &&
                section.id &&
                ((section.id === 'options' && (this.showAllOptions || index < 4)) || section.id !== 'options') &&
                // Don't show when all the vehicles have empty values
                !(
                    feature &&
                    section.id !== 'electricFeatures' &&
                    section.id !== 'options' &&
                    this.itemList.filter((item) => !item[feature.key]).length === this.maxItems
                )
            );
        },
        canShowRating(feature, item) {
            return (
                feature &&
                feature.type !== 'text' &&
                item &&
                item.rawValue >= 0 &&
                item.value &&
                item.value !== '-' &&
                feature.ratingType !== 'seat'
            );
        },
        onAddVehicleClick() {
            if (this.busy) {
                return;
            }
            this.pushGaEvent('Compare page', 'click', 'add vehicle click');

            this.$emit('add-vehicle-click');
        },
        onMenuClick(data) {
            this.$emit('menu-click', data);
        },
        onSeeAllClick() {
            this.showAllOptions = true;
        },
        onCompareQuotesClick() {
            this.pushGaEvent('Compare page', 'click', 'CTA button click to request quotes');
            this.$emit('compare-quotes-click');
        },
        setColspan() {
            if (breakpoint(1023, 'max')) {
                this.colspan = this.items.length;
                this.isMobile = true;
            } else {
                this.colspan = this.itemList.length;
                this.isMobile = false;
            }
        },
        throttledSetColspan: throttle(function() {
            this.setColspan();
        }, 60),
        shouldHideLastColumn(index) {
            return index === this.itemList.length - 1 && this.isMobile && this.newItemIsInsertable;
        },
        getOptionIcon(item, feature) {
            if (!item || !item.options || !feature) {
                return [null, null];
            }

            const option = item.options.find((opts) => opts.id === feature.key);

            if (!option) {
                return ['ds-close-default', 'tw-text-feedback-danger'];
            }

            if (option.optional) {
                return ['ds-checkmark-empty', 'tw-text-brand-accent'];
            }

            return [null, null];
        },
        getOptionText(item, feature) {
            const dash = this.$options.filters.dash();

            if (!item || !item.options || !feature) {
                return dash;
            }

            const option = item.options.find((opts) => opts.id === feature.key);

            if (!option) {
                return this.$t('compare-table.not-available');
            }

            if (option.optional) {
                return this.$t('compare-table.standard');
            } else if (option.price) {
                return option.price;
            }

            return dash;
        },
        getElectricIcon(item, feature) {
            if (!item || !item.electricFeatures || !item.isElectric || !feature) {
                return [null, null];
            }

            const option = item.electricFeatures.find((opt) => opt.id === feature.key);

            if (!option) {
                return ['ds-close-default', 'tw-text-feedback-danger'];
            }

            if (option.id === 'fastCharge' || option.id === 'heatPump') {
                return ['ds-checkmark-empty', 'tw-text-brand-accent'];
            }

            let optionIcon = '';

            if (['chademo', 'combo', 'tesla', 'type-1', 'type-2', 'type-3'].includes(option.icon)) {
                optionIcon = `ds-${option.icon}`;
            }

            return [optionIcon, 'tw-text-base-subtle'] || [null, null];
        },
        getElectricText(item, feature) {
            const dash = this.$options.filters.dash();

            if (isEmpty(item)) {
                return dash;
            }
            if (!item.isElectric) {
                return this.$t('compare-table.electric-not-available-fuel-type');
            }
            if (!feature) {
                return this.$t('compare-table.not-available');
            }

            const option = item.electricFeatures.find((opt) => opt.id === feature.key);

            if (!option) {
                return this.$t('compare-table.not-available');
            }

            return option.text;
        },
        hasDimensions(dimensions) {
            if (
                !dimensions ||
                !dimensions.width ||
                !dimensions.width.value ||
                !dimensions.height ||
                !dimensions.height.value ||
                !dimensions.length ||
                !dimensions.length.value
            ) {
                return false;
            }

            return true;
        },
        getVehicleWithIndex(vehicle, index) {
            return { ...vehicle, index };
        },
        getTableHeadHeight() {
            const dkTableTheadElm = this.$el.querySelector('.dk-table__table > thead');

            if (dkTableTheadElm) {
                return dkTableTheadElm.offsetHeight;
            }

            return 0;
        },
        throttledScrollSpy: throttle(function() {
            this.scrollSpy();
        }, 30),
        scrollSpy() {
            const sectionElms = Array.from(this.$el.querySelectorAll('.compare-table__section-name[id]'));
            const targetElms = Array.from(this.$el.querySelectorAll('.dk-table__sticky-head .dk-tabs__control'));
            const toggledClass = 'dk-tabs__control--selected';
            const offset = this.getTableHeadHeight() + 20;

            sectionElms.forEach((sectionElm) => {
                if (!sectionElm) {
                    return;
                }
                const { top } = sectionElm.getBoundingClientRect();

                if (top > 0) {
                    if (top <= offset) {
                        targetElms.forEach((targetElm) => targetElm.classList.remove(toggledClass));

                        const targetElm = this.$el.querySelector(
                            `.dk-table__sticky-head a[href="#${this.tabIdPrefix}${sectionElm.id}"]`
                        );

                        if (targetElm) {
                            targetElm.parentNode.classList.add(toggledClass);
                        }
                    }
                }
            });
        },
    },
};
</script>
